/**
 * @version 1.0.0
 * @created 2026-02-02
 * @description [Service] Supabase Server clients for server-side operations
 *              Includes singleton clients and SSR/API route helpers
 */
// Next
// Supabase
import { NextRequest, NextResponse } from "next/server";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { createServerClient, serializeCookieHeader } from "@supabase/ssr";
// Sources
import { NextContext } from "@/source/types";

declare global {
  var SUPABASE_ANNO: SupabaseClient | undefined;
  var SUPABASE_ROLE: SupabaseClient | undefined;
}

/**
 * Validates and returns Supabase environment variables
 * @throws Error if required environment variables are missing
 * @returns Object containing validated URL and key
 */
const getSupabaseEnv = () => {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!url || !key) {
    throw new Error("[Supabase] Missing required environment variables: NEXT_PUBLIC_SUPABASE_URL, NEXT_PUBLIC_SUPABASE_ANON_KEY");
  }

  return { url, key };
};

/**
 * Extracts all cookies from Next.js request object
 * @param req - Next.js request object with cookies
 * @returns Array of cookie objects with name and value
 */
const getCookiesFromRequest = (req: NextContext["req"]) => {
  return Object.keys(req.cookies).map((name) => ({
    name,
    value: req.cookies[name] || "",
  }));
};

/**
 * Supabase Anon client - uses anon key for basic server-side operations
 * Use this for:
 * - Basic read operations
 * - Public data access
 * - Non-privileged operations
 *
 * @returns Supabase client with anon key
 */
export const createAnonClient = (): SupabaseClient => {
  if (!globalThis.SUPABASE_ANNO) {
    const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    if (!url || !key) {
      throw new Error("[ANON client] Missing required environment variables: NEXT_PUBLIC_SUPABASE_URL, NEXT_PUBLIC_SUPABASE_ANON_KEY");
    }

    globalThis.SUPABASE_ANNO = createClient(url, key, {
      auth: { autoRefreshToken: false, persistSession: true },
    });
  }

  return globalThis.SUPABASE_ANNO;
};

/**
 * Supabase Service Role client - bypasses RLS for admin operations
 * Use this for:
 * - Updating auth.sessions directly
 * - Admin-level DB operations
 *
 * @returns Supabase client with service role key
 */
export const createRoleClient = (): SupabaseClient => {
  if (!globalThis.SUPABASE_ROLE) {
    const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const key = process.env.SUPABASE_SERVICE_ROLE_KEY;

    if (!url || !key) {
      throw new Error("[ROLE client] Missing required environment variables: NEXT_PUBLIC_SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY");
    }

    globalThis.SUPABASE_ROLE = createClient(url, key, {
      auth: { autoRefreshToken: false, persistSession: false },
    });
  }

  return globalThis.SUPABASE_ROLE;
};

/**
 * Create Supabase SSR client for getServerSideProps
 * Uses appendHeader for cookie management (safe for single execution context)
 *
 * Use this in:
 * - getServerSideProps (page authentication)
 * - Server-side page rendering
 *
 * @param context - Next.js context from getServerSideProps
 * @returns Supabase client configured for SSR with cookie handling
 *
 * @example
 * ```typescript
 * export async function getServerSideProps(context: NextContext) {
 *   const supabase = createSSRClient(context);
 *   const { data: user } = await supabase.auth.getUser();
 * }
 * ```
 */
export const createSSRClient = (context: NextContext): SupabaseClient => {
  const { req, res } = context;
  const { url, key } = getSupabaseEnv();

  return createServerClient(url, key, {
    auth: { autoRefreshToken: false, persistSession: true },
    cookies: {
      getAll: () => getCookiesFromRequest(req),
      setAll(cookiesToSet) {
        cookiesToSet.forEach(({ name, value, options }) => {
          res.appendHeader("Set-Cookie", serializeCookieHeader(name, value, options));
        });
      },
    },
  });
};

/**
 * Create Supabase API client for API routes
 * Uses setHeader with cookie merging (safe for complex middleware chains)
 *
 * Use this in:
 * - API routes (pages/api/)
 * - Authentication endpoints (signin/signout)
 * - Middleware with multiple cookie operations
 *
 * @param context - Next.js context with req/res
 * @returns Supabase client configured for API routes with cookie handling
 *
 * @example
 * ```typescript
 * export default async function handler(req: NextApiRequest, res: NextApiResponse) {
 *   const supabase = createAPIClient({ req, res });
 *   await supabase.auth.signInWithPassword({ email, password });
 * }
 * ```
 */
export function createAPIClient(context: NextContext): SupabaseClient {
  const { req, res } = context;
  const { url, key } = getSupabaseEnv();

  return createServerClient(url, key, {
    auth: { autoRefreshToken: false, persistSession: true },
    cookies: {
      getAll: () => getCookiesFromRequest(req),
      setAll(cookiesToSet) {
        // Merge with existing cookies to prevent overwriting
        const existingCookies = res.getHeader("Set-Cookie") || [];
        const existingArray = Array.isArray(existingCookies) ? existingCookies.filter((c): c is string => typeof c === "string") : typeof existingCookies === "string" ? [existingCookies] : [];

        const newCookies = cookiesToSet.map(({ name, value, options }) => serializeCookieHeader(name, value, options));

        // Set all cookies at once (replaces existing Set-Cookie header)
        res.setHeader("Set-Cookie", [...existingArray, ...newCookies]);
      },
    },
  });
}

export function createMiddlewareClient(request: NextRequest, response: NextResponse) {
  const { url, key } = getSupabaseEnv();

  return createServerClient(url, key, {
    auth: { autoRefreshToken: false, persistSession: true },
    cookies: {
      getAll() {
        return request.cookies.getAll();
      },
      setAll(cookiesToSet) {
        cookiesToSet.forEach(({ name, value }) => request.cookies.set(name, value));
        cookiesToSet.forEach(({ name, value, options }) => response.cookies.set(name, value, options));
      },
    },
  });
};
